/**
 * Minimum password length configured on the Cognito user pool
 * (amplify/backend/auth/www0437f888/cli-inputs.json: passwordPolicyMinLength).
 * The pool has no character-class requirements, so length is the only client-side rule.
 */
export const COGNITO_PASSWORD_MIN_LENGTH = 8;

export const GENERIC_CHANGE_PASSWORD_ERROR = 'Could not change password.';
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign out and sign in again, then retry.';

type CognitoLikeError = { code?: unknown; name?: unknown; message?: unknown };

const readError = (err: unknown): { code: string; message: string } => {
    if (typeof err === 'string') {
        return { code: '', message: err };
    }

    const { code, name, message } = (err ?? {}) as CognitoLikeError;

    return {
        code: typeof code === 'string' ? code : typeof name === 'string' ? name : '',
        message: typeof message === 'string' ? message : '',
    };
};

/**
 * Turns the error rejected by Auth.currentAuthenticatedUser / Auth.changePassword into a
 * message that tells the user what to do. Cognito reports every failure with a code and a
 * message, so this maps the codes we expect and falls back to the raw message otherwise.
 */
export const describeChangePasswordError = (err: unknown): string => {
    const { code, message } = readError(err);
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('not authenticated') || lowerMessage.includes('token has expired') || lowerMessage.includes('invalid access token')) {
        return SESSION_EXPIRED_MESSAGE;
    }

    switch (code) {
        case 'NotAuthorizedException':
            return 'The current password you entered is incorrect.';
        case 'InvalidPasswordException': {
            // Cognito phrases these as "Password did not conform with policy: <detail>"
            const detail = message.replace(/^password did not conform with policy:\s*/i, '').replace(/\.$/, '');

            return detail
                ? `The new password does not meet the password requirements: ${detail}.`
                : 'The new password does not meet the password requirements.';
        }
        case 'LimitExceededException':
        case 'TooManyRequestsException':
            return 'Too many attempts. Please wait a few minutes and try again.';
        case 'NetworkError':
            return 'Could not reach the authentication service. Check your connection and try again.';
        default:
            return message ? `Could not change password: ${message}` : GENERIC_CHANGE_PASSWORD_ERROR;
    }
};

/**
 * Client-side check of the new password pair, mirroring the pool policy so the user is told
 * about a problem before the request is sent. Returns null when there is nothing to report.
 */
export const getNewPasswordProblem = (newPassword: string, repeatNewPassword: string): string | null => {
    if (newPassword.length === 0 && repeatNewPassword.length === 0) {
        return null;
    }

    if (newPassword.length < COGNITO_PASSWORD_MIN_LENGTH) {
        return `Password must be at least ${COGNITO_PASSWORD_MIN_LENGTH} characters`;
    }

    if (newPassword !== repeatNewPassword) {
        return 'Passwords do not match';
    }

    return null;
};
